import { AyurmaStrings } from "./en";

// Simplified Chinese. All zh* URL variants resolve here (see resolveReportLang).
// Note: no bundled font covers CJK — these glyphs render through the system
// fallback stack declared on the white-label page root.
export const ayurmaZh: AyurmaStrings = {
  sections: {
    coverResults: "代谢评估",
    caloricNutrition: "热量平衡与营养",
    nutritionGuide: "营养指南",
    movementPlan: "运动计划",
    sourcesDisclaimer: "科学文献",
  },

  cover: {
    title: "代谢评估",
    subtitle: "静息代谢率（RMR）报告",
    resultsHeading: "您的结果",
  },

  patientInfo: {
    name: "姓名",
    testType: "测试类型",
    testTypeValue: "静息代谢评估（RMR）",
    gender: "性别",
    date: "日期",
    weight: "体重",
    activityLevel: "活动水平",
    height: "身高",
    goal: "目标",

    units: {
      kg: "公斤",
      lbs: "磅",
      cm: "厘米",
      ft: "英尺",
      in: "英寸",
    },

    genders: {
      male: "男",
      female: "女",
    },

    exerciseFrequency: {
      none: "无",
      perWeek: "每周 {count} 次",
    },

    goals: {
      muscleDevelopment: "增肌",
      strengthDevelopment: "力量提升",
      endurancePerformance: "耐力运动表现",
      longevity: "长寿",
      bodyRecomposition: "身体重塑",
      dynamicPerformance: "动态运动表现",
    },
  },

  glossary: {
    heading: "关键指标（静息）",
    terms: {
      RER: {
        term: "RER",
        text: "呼吸交换率 — 显示身体以碳水化合物还是脂肪作为燃料",
      },
      VO2: {
        term: "VO2",
        text: "脂肪氧化 — 静息状态下脂肪的供能情况",
      },
      VCO2: {
        term: "VCO2",
        text: "碳水化合物氧化 — 静息状态下碳水化合物的供能情况",
      },
      VT: {
        term: "VT",
        text: "潮气量 — 单次呼吸进出的空气量",
      },
      BF: {
        term: "BF",
        text: "呼吸频率 — 每分钟呼吸次数",
      },
      VE: {
        term: "VE",
        text: "分钟通气量 — 每分钟进出肺部的空气量",
      },
    },
  },

  metrics: {
    ventilationEfficiency: {
      name: "通气效率",
      description: "您的肺部吸收氧气并排出二氧化碳的能力",
    },
    breathingCoordination: {
      name: "呼吸协调性",
      description: "您在静息状态下保持规律且高效呼吸的能力",
    },
    fatBurningEfficiency: {
      name: "脂肪燃烧效率",
      description: "您的细胞将脂肪作为燃料来源的能力",
    },
    metabolicRate: {
      name: "代谢率",
      description: "您的身体在静息状态下消耗的热量",
    },
    metabolicFlexibility: {
      name: "代谢灵活性",
      description: "您在脂肪与碳水化合物之间快速切换燃料来源的能力",
    },
    sympatheticParasympathetic: {
      name: "交感 / 副交感神经激活",
      description: "您自主神经系统两大分支之间的平衡",
    },
    lungUtilization: {
      name: "肺容量利用率",
      description: "您在静息状态下使用的肺容量比例",
    },
    heartRateVariability: {
      name: "心率变异性（HRV）",
      description: "您的心血管系统在静息状态下的功能",
    },
    oxygenDelivery: {
      name: "氧气输送",
      description: "",
    },
    respiratoryCapacity: {
      name: "呼吸容量",
      description: "",
    },
  },

  severity: {
    howToRead: "如何解读您的分数",
    range: { low: "低", high: "高" },
    ticks: {
      severe: "严重受限",
      limitation: "受限",
      neutral: "中性",
      good: "良好",
      excellent: "优秀",
    },
    bands: {
      severe: { label: "严重", range: "0–20%" },
      limitation: { label: "受限", range: "21–40%" },
      neutral: { label: "中性", range: "41–60%" },
      good: { label: "良好", range: "61–80%" },
      excellent: { label: "优秀", range: "81–100%" },
    },
  },

  caloric: {
    heading: "热量平衡",
    burnTitle: "您消耗的热量（千卡/天）",
    eatTitle: "您应摄入的热量（千卡/天）",
    trainingDays: "训练日",
    routineDays: "日常活动日",
  },

  macros: {
    heading: "宏量营养素记录",
    names: { protein: "蛋白质", carbs: "碳水化合物", fat: "脂肪" },
    units: { kcal: "千卡", g: "克" },
    trainingDays: "训练日",
    routineDays: "日常活动日",
  },

  recommendations: {
    heading: "建议",
    rows: {
      mealFrequency: "进餐频率",
      timing: "进餐时间",
      hydration: "水分补充",
      infusions: "汤饮",
      aroma: "芳香疗法",
    },
  },

  nutritionGuide: {
    heading: "营养指南",
    subtitle: "由您的 AyurMa 从业者定制。",
    eatLimitAvoidHeading: "宜食 / 限食 / 忌食",
    columns: {
      eat: "宜食",
      limit: "限食",
      avoid: "忌食",
    },
    otherRecommendations: "其他建议",
    supplements: "补充剂",
  },

  movement: {
    heading: "运动计划",
    subtitle: "每类活动每周两天。",
    yoga: {
      label: "瑜伽",
      hint: "请填写建议的体式、次数与练习。",
      columns: {
        posture: "体式 / 练习",
        reps: "次数 / 时长",
        notes: "备注",
      },
    },
    aerobic: {
      label: "有氧运动",
      rows: {
        briskWalking: "快走",
        swimming: "游泳",
        cycling: "骑行",
        other: "其他",
      },
    },
    strength: {
      label: "力量训练",
      rows: {
        floorExercises: "地板动作",
        calisthenics: "自重训练",
        freeWeights: "自由重量",
        other: "其他",
      },
    },
  },

  footer: {
    brandLabel: "{company} · 代谢健康",
  },

  qr: {
    caption: "扫码了解更多关于您的交感 / 副交感神经激活",
  },

  sources: {
    heading: "科学文献",
    originalLanguageNote: "参考文献按其原始出版语言列出。",
    disclaimerLabel: "免责声明：",
    disclaimerBody:
      "本评估/报告仅供参考，在任何情况下均不应被视为专业医疗建议、诊断或治疗的替代。在根据本评估/报告所提供的信息开始任何运动计划和/或改变饮食和/或习惯之前，您需要咨询您的医生和/或家庭医生。{company} 不保证本评估/报告会使您的健康与体能状况得到任何改善。您同意，参与任何训练计划、体育锻炼或活动，可能因其性质、频率、强度和持续时间而增加身体受伤的风险。您同意，若您参与任何训练计划、体育锻炼或活动，均由您自行承担风险，并承担您可能遭受的任何及所有伤害和/或损害。",
  },

  langPicker: {
    label: "报告语言",
  },
};
